import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PreRegistration } from '@cloudsign/shared';
import type { PreRegistrationStatus } from '@cloudsign/shared';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorPanel, errorMessage } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Button, EmptyRow, FilterBar, PageHeader, Pager, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { DateRangePicker } from '../../components/phase2';
import type { DateRange } from '../../components/phase2';
import { Dialog } from '../fields-notices/Dialog';
import { CreateDialog } from './CreateDialog';
import { DetailDrawer } from './DetailDrawer';
import { ProgressIcons, StatusLabel } from './Progress';
import { PreRegistrationPage, STATUS_TABS, preKey, useActiveGroups, useAllSites } from './api';
import { todayIn } from '../../lib/zoneTime';

const PAGE_SIZE = 25;

export function PreRegistrationsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const canCreate = useCan('today.sign_in_out');
  const canApprove = useCan('today.approve_preregistrations');
  const list = useListState();
  const sites = useAllSites();
  const groups = useActiveGroups();
  const [siteId, setSiteId] = useState('');
  const [range, setRange] = useState<DateRange>(() => {
    const d = todayIn('Europe/London');
    return { from: d, to: d };
  });
  const [tab, setTab] = useState<PreRegistrationStatus | 'all'>('all');
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<PreRegistration | null>(null);
  const [reason, setReason] = useState('');

  const query = {
    siteId: siteId || undefined,
    status: tab === 'all' ? undefined : tab,
    from: range.from || undefined,
    to: range.to || undefined,
    search: list.search || undefined,
    page: list.page,
    pageSize: PAGE_SIZE,
  };
  const q = useQuery({
    queryKey: [...preKey, 'list', query],
    queryFn: () => api.get('/pre-registration', { query, schema: PreRegistrationPage }),
    placeholderData: keepPreviousData,
  });

  const siteList = sites.data?.items ?? [];
  const siteById = new Map(siteList.map((s) => [s.id, s]));
  const decide = useMutation({
    mutationFn: (v: { id: string; decision: 'approve' | 'reject'; reason: string }) =>
      api.post(`/pre-registration/${v.id}/decision`, { body: { decision: v.decision, reason: v.reason }, schema: PreRegistration }),
    onSuccess: (r) => {
      toast(r.status === 'rejected' ? t('prereg.decision.rejected') : t('prereg.decision.approved'));
      setRejecting(null);
      setReason('');
      void qc.invalidateQueries({ queryKey: preKey });
    },
  });

  const items = q.data?.items ?? [];
  const rowSite = (p: PreRegistration) => siteById.get(p.siteId);

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <PageHeader
        title={t('prereg.title')}
        description={t('prereg.description')}
        actions={
          canCreate ? (
            <Button variant="primary" disabled={siteList.length === 0 || !groups.data} onClick={() => setCreating(true)}>
              {t('prereg.create.open')}
            </Button>
          ) : null
        }
      />

      <FilterBar columns="sm:grid-cols-3">
        <TextField label={t('prereg.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('prereg.field.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('prereg.allSites')}</option>
          {siteList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <DateRangePicker
          label={t('prereg.dates')}
          value={range}
          onChange={(r) => {
            setRange(r);
            list.resetPage();
          }}
        />
      </FilterBar>

      <div role="group" aria-label={t('prereg.tabs')} className="flex flex-wrap gap-2">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={tab === s}
            onClick={() => {
              setTab(s);
              list.resetPage();
            }}
            className={`min-h-9 rounded-full border px-3.5 text-sm font-medium ${tab === s ? 'border-brand bg-brand text-white' : 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'}`}
          >
            {t(`prereg.tab.${s}`)}
          </button>
        ))}
      </div>

      {decide.error ? <Alert>{errorMessage(decide.error)}</Alert> : null}

      {q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('prereg.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('prereg.col.visitor')}</Th>
                <Th>{t('prereg.col.expected')}</Th>
                <Th>{t('prereg.col.host')}</Th>
                <Th>{t('prereg.col.status')}</Th>
                <Th>{t('prereg.col.progress')}</Th>
                {canApprove ? <Th>{t('prereg.col.actions')}</Th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? (
                <EmptyRow
                  cols={canApprove ? 6 : 5}
                  action={
                    canCreate ? (
                      <Button variant="primary" onClick={() => setCreating(true)}>
                        {t('prereg.create.open')}
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                items.map((p) => (
                  <tr key={p.id} className="align-top">
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => setOpen(p.id)} className="text-left font-medium text-brand hover:underline">
                        {p.visitorName}
                      </button>
                      {p.visitorCompany ? <div className="text-xs text-slate-600">{p.visitorCompany}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-sm">{formatDateTime(p.expectedAt, rowSite(p)?.timeZone)}</td>
                    <td className="px-3 py-2 text-sm">{p.hostName ?? '-'}</td>
                    <td className="px-3 py-2">
                      <StatusLabel status={p.status} />
                    </td>
                    <td className="px-3 py-2">
                      <ProgressIcons progress={p.progress} />
                    </td>
                    {canApprove ? (
                      <td className="px-3 py-2">
                        {p.status === 'pending_approval' ? (
                          <div className="flex flex-wrap gap-2">
                            <Button disabled={decide.isPending} aria-label={t('prereg.decision.approveFor', { name: p.visitorName })} onClick={() => decide.mutate({ id: p.id, decision: 'approve', reason: '' })}>
                              {t('prereg.decision.approve')}
                            </Button>
                            <Button disabled={decide.isPending} aria-label={t('prereg.decision.rejectFor', { name: p.visitorName })} onClick={() => setRejecting(p)}>
                              {t('prereg.decision.reject')}
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </TableWrap>
          <Pager page={q.data?.page ?? 1} pageSize={PAGE_SIZE} total={q.data?.total ?? 0} onPage={list.setPage} />
        </>
      )}

      {creating && sites.data && groups.data ? <CreateDialog sites={siteList} groups={groups.data} onClose={() => setCreating(false)} /> : null}
      {open ? <DetailDrawer id={open} site={siteById.get(items.find((p) => p.id === open)?.siteId ?? '')} onClose={() => setOpen(null)} /> : null}
      {rejecting ? (
        <Dialog title={t('prereg.decision.rejectTitle', { name: rejecting.visitorName })} onClose={() => setRejecting(null)}>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              decide.mutate({ id: rejecting.id, decision: 'reject', reason });
            }}
          >
            <TextField label={t('prereg.decision.reason')} hint={t('prereg.decision.reasonHint')} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button type="button" onClick={() => setRejecting(null)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={decide.isPending}>
                {t('prereg.decision.reject')}
              </Button>
            </div>
          </form>
        </Dialog>
      ) : null}
      <p className="text-sm text-slate-600">
        <Link to="/today" className="text-brand underline">
          {t('prereg.backToToday')}
        </Link>
      </p>
    </div>
  );
}
